import { useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
   AppShell,
   Group,
   Title,
   Button,
   Text,
   Menu,
   UnstyledButton,
   Avatar,
   Divider,
   NavLink,
   Stack,
   Burger,
   Box,
} from "@mantine/core";
import {
   IconUsers,
   IconFileCheck,
   IconCreditCard,
   IconPackage,
   IconCurrencyDollar,
   IconChevronDown,
   IconLogout,
   IconUser,
   IconChevronLeft,
   IconChevronRight,
   IconShieldLock,
   IconReportAnalytics,
} from "@tabler/icons-react";
import { useAuth } from "../contexts/AuthContext";
import { AppName, Logo } from "../lib/data";

interface NavItem {
   label: string;
   path: string;
   icon: any;
}

const navItems: NavItem[] = [
   { label: "Users", path: "/dashboard/users", icon: IconUsers },
   { label: "KYC", path: "/dashboard/kyc", icon: IconFileCheck },
   { label: "Loan Products", path: "/dashboard/loan-products", icon: IconPackage },
   { label: "Loan Requests", path: "/dashboard/loan-requests", icon: IconCreditCard },
   { label: "Fees", path: "/dashboard/fees", icon: IconCurrencyDollar },
   { label: "Reports", path: "/dashboard/reports", icon: IconReportAnalytics },
   { label: "Account", path: "/dashboard/account", icon: IconShieldLock },
];

export default function DashboardLayout() {
   const { user, logout } = useAuth();
   const navigate = useNavigate();
   const location = useLocation();
   const [sidebarOpened, setSidebarOpened] = useState(true);

   const handleLogout = async () => {
      try {
         await logout();
         navigate("/login");
      } catch (error) {
         console.error("Logout error:", error);
      }
   };

   const handleNavigate = (path: string) => {
      navigate(path);
   };

   const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");

   return (
      <AppShell
         header={{ height: 70 }}
         navbar={{
            width: 260,
            breakpoint: "sm",
            collapsed: { mobile: !sidebarOpened, desktop: !sidebarOpened },
         }}
         padding="md"
         style={
            {
               "--mantine-navbar-width": sidebarOpened ? "260px" : "80px",
            } as React.CSSProperties
         }
      >
         {/* Top Header */}
         <AppShell.Header
            style={{
               background: "white",
               borderBottom: "1px solid #E0E0E0",
            }}
         >
            <Group h="100%" px="xl" justify="space-between">
               {/* Left: Burger Menu & Logo */}
               <Group gap="md">
                  <Burger
                     opened={sidebarOpened}
                     onClick={() => setSidebarOpened(!sidebarOpened)}
                     size="sm"
                     style={{ color: "#679369" }}
                  />
                  {sidebarOpened && (
                     <Link to="/dashboard" style={{ textDecoration: "none" }}>
                        <Group gap="sm">
                           <img src={Logo} alt={`${AppName} Logo`} style={{ height: "32px" }} />
                           <Title order={4} style={{ color: "#679369", margin: 0 }} className="font-bold">
                              {AppName}
                           </Title>
                        </Group>
                     </Link>
                  )}
               </Group>

               {/* Right: User Menu */}
               <Menu shadow="md" width={200} position="bottom-end">
                  <Menu.Target>
                     <UnstyledButton>
                        <Group gap="xs">
                           <Avatar
                              size={36}
                              radius="xl"
                              style={{
                                 background: "#679369",
                                 color: "white",
                              }}
                           >
                              {user?.email?.[0]?.toUpperCase() || "U"}
                           </Avatar>
                           {sidebarOpened && (
                              <>
                                 <Text size="sm" fw={500} style={{ color: "#333" }} visibleFrom="sm">
                                    {user?.email}
                                 </Text>
                                 <IconChevronDown size={16} style={{ color: "#666" }} />
                              </>
                           )}
                        </Group>
                     </UnstyledButton>
                  </Menu.Target>

                  <Menu.Dropdown>
                     <Menu.Label>
                        <Group gap="xs">
                           <IconUser size={16} style={{ color: "#679369" }} />
                           <Text size="sm" fw={500}>
                              {user?.email}
                           </Text>
                        </Group>
                     </Menu.Label>
                     <Divider />
                     <Menu.Item leftSection={<IconLogout size={18} />} onClick={handleLogout} color="red">
                        Logout
                     </Menu.Item>
                  </Menu.Dropdown>
               </Menu>
            </Group>
         </AppShell.Header>

         {/* Left Sidebar */}
         <AppShell.Navbar
            style={{
               background: "white",
               borderRight: "1px solid #E0E0E0",
               width: sidebarOpened ? 260 : 80,
               transition: "width 0.2s ease",
            }}
         >
            <Stack gap={0} p="md" h="100%">
               {/* Navigation Links */}
               <Stack gap={4} style={{ flex: 1 }}>
                  {navItems.map((item) => {
                     const IconComponent = item.icon;
                     const active = isActive(item.path);

                     return (
                        <NavLink
                           key={item.path}
                           label={sidebarOpened ? item.label : undefined}
                           leftSection={<IconComponent size={20} />}
                           onClick={() => handleNavigate(item.path)}
                           active={active}
                           title={!sidebarOpened ? item.label : undefined}
                           style={{
                              borderRadius: "8px",
                              backgroundColor: active ? "#F0F9F0" : "transparent",
                              color: active ? "#679369" : "#666",
                              fontWeight: active ? 600 : 400,
                           }}
                           styles={{
                              label: {
                                 fontSize: "14px",
                              },
                              root: {
                                 justifyContent: sidebarOpened ? "flex-start" : "center",
                              },
                           }}
                        />
                     );
                  })}
               </Stack>

               {/* Toggle Sidebar Button */}
               <Box mt="auto" pt="md" style={{ borderTop: "1px solid #E0E0E0" }}>
                  <Button
                     variant="subtle"
                     fullWidth
                     leftSection={sidebarOpened ? <IconChevronLeft size={18} /> : <IconChevronRight size={18} />}
                     onClick={() => setSidebarOpened(!sidebarOpened)}
                     style={{
                        color: "#679369",
                        justifyContent: sidebarOpened ? "flex-start" : "center",
                        paddingLeft: sidebarOpened ? "12px" : "0",
                     }}
                     title={sidebarOpened ? "Collapse sidebar" : "Expand sidebar"}
                  >
                     {sidebarOpened ? "Collapse" : ""}
                  </Button>
               </Box>
            </Stack>
         </AppShell.Navbar>

         {/* Main Content */}
         <AppShell.Main style={{ background: "#FAFAFA" }}>
            <AnimatePresence mode="wait">
               <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
               >
                  <Outlet />
               </motion.div>
            </AnimatePresence>
         </AppShell.Main>
      </AppShell>
   );
}
